import type { CSSProperties, ReactElement } from "react";
import { ChevronsDown, ChevronsRight } from "lucide-react";
import { color, layout, radius, space } from "@tokens/index";
import { Text, useViewport } from "@primitives/index";
import type { FieldDiff } from "./payload";

const listStyle: CSSProperties = {
  marginTop: space[1],
  display: "flex",
  flexDirection: "column",
  gap: space[2],
  maxWidth: layout.proseMaxWidth,
  minWidth: 0,
};

const fieldStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: space[1],
  minWidth: 0,
};

function pairStyle(stacked: boolean): CSSProperties {
  return {
    display: "flex",
    flexDirection: stacked ? "column" : "row",
    flexWrap: stacked ? "nowrap" : "wrap",
    gap: space[2],
    alignItems: "stretch",
    minWidth: 0,
  };
}

const arrowStyle: CSSProperties = {
  alignSelf: "center",
  color: color.fg.subtle,
  flexShrink: 0,
  display: "inline-flex",
};

/**
 * Before/After pairs for one change event. Pairs sit side by side on
 * `medium` and wider and stack on `compact`. Values wrap at word
 * boundaries; an unbreakable value (a long id) scrolls inside its box
 * instead of widening the row.
 */
export function ChangeDiff({ diffs }: { readonly diffs: readonly FieldDiff[] }): ReactElement {
  const { isCompact } = useViewport();
  const multi = diffs.length > 1;
  const Arrow = isCompact ? ChevronsDown : ChevronsRight;
  return (
    <div style={listStyle}>
      {diffs.map((diff) => (
        <div key={diff.label} style={fieldStyle}>
          {multi ? (
            <Text role="caption" tone="subtle">
              {diff.label}
            </Text>
          ) : null}
          <div style={pairStyle(isCompact)}>
            <DiffSide tone="old" label="Before" value={diff.before} />
            <span aria-hidden="true" style={arrowStyle}>
              <Arrow size={14} strokeWidth={2.25} />
            </span>
            <DiffSide tone="new" label="After" value={diff.after} />
          </div>
        </div>
      ))}
    </div>
  );
}

function DiffSide({
  tone,
  label,
  value,
}: {
  readonly tone: "old" | "new";
  readonly label: string;
  readonly value: string;
}): ReactElement {
  const fill = tone === "old" ? color.status.dangerBg : color.status.successBg;
  const edge = tone === "old" ? color.status.dangerFg : color.status.successFg;
  return (
    <div
      style={{
        flex: "1 1 200px",
        minWidth: 0,
        paddingInline: space[3],
        paddingBlock: space[2],
        borderRadius: radius.md,
        background: fill,
        borderLeft: `2px solid ${edge}`,
        overflowX: "auto",
      }}
    >
      <Text role="caption" tone="subtle" style={{ display: "block", marginBottom: space[1] }}>
        {label}
      </Text>
      <Text
        role="bodySm"
        style={{
          display: "block",
          whiteSpace: "pre-wrap",
          overflowWrap: "normal",
          wordBreak: "normal",
          color: color.fg.default,
        }}
      >
        {value}
      </Text>
    </div>
  );
}
